import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import {
  Box,
  Button,
  ButtonProps,
  Card,
  CardActionArea,
  CardContent,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { FC, FormEvent, useCallback, useState } from 'react';
import { ErrorCode, FileRejection, useDropzone } from 'react-dropzone';

import {
  MAX_FINANCIAL_DOCUMENTS,
  MAX_FINANCIAL_DOCUMENT_SIZE_BYTES,
  MAX_FINANCIAL_DOCUMENT_SIZE_MB,
} from '../../../../domain/document-collection';
import { SubmitWorkflowButton } from '../../../workflow/components/submit-workflow-button';

const PDF_MIME_TYPE = 'application/pdf';

const TOO_MANY_FILES_MESSAGE = `You can upload up to ${MAX_FINANCIAL_DOCUMENTS} files.`;
const FILE_TOO_LARGE_MESSAGE = `Each file must be ${MAX_FINANCIAL_DOCUMENT_SIZE_MB} MB or smaller.`;
const INVALID_TYPE_MESSAGE = 'Only PDF files can be uploaded.';

type Props = {
  submitting: boolean;
  progress?: number;
  progressComputable?: boolean;
  disabled?: boolean;
  onSubmit: (files: File[]) => void;
};

const identity = (file: File) => `${file.name}:${file.size}:${file.lastModified}`;

const rejectionMessages = (rejections: FileRejection[]): string[] => {
  const codes = new Set(rejections.flatMap(({ errors }) => errors.map(({ code }) => code)));
  const messages = [
    codes.has(ErrorCode.FileTooLarge) ? FILE_TOO_LARGE_MESSAGE : null,
    codes.has(ErrorCode.FileInvalidType) ? INVALID_TYPE_MESSAGE : null,
  ].filter((message): message is string => message !== null);

  return messages.length > 0 ? messages : [INVALID_TYPE_MESSAGE];
};

const FinancialDocumentsUpload: FC<Props> = ({
  submitting,
  progress = 0,
  progressComputable = true,
  disabled = false,
  onSubmit,
}: Props) => {
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      const messages = rejections.length > 0 ? rejectionMessages(rejections) : [];
      const seen = new Set(files.map(identity));
      // re-selecting a file already in the list is a no-op rather than a duplicate upload
      const added = accepted.filter((file) => {
        const key = identity(file);
        const isNew = !seen.has(key);
        seen.add(key);

        return isNew;
      });
      const room = Math.max(MAX_FINANCIAL_DOCUMENTS - files.length, 0);

      if (added.length > room) {
        messages.push(TOO_MANY_FILES_MESSAGE);
      }

      setFiles([...files, ...added.slice(0, room)]);
      setErrors(messages);
    },
    [files],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: PDF_MIME_TYPE,
    maxSize: MAX_FINANCIAL_DOCUMENT_SIZE_BYTES,
    disabled: submitting || disabled,
  });

  const removeFile = (index: number) => () => {
    setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index));
    setErrors([]);
  };

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      onSubmit(files);
    },
    [files, onSubmit],
  );

  return (
    <form onSubmit={handleSubmit}>
      <Card variant="outlined">
        <CardActionArea {...getRootProps()}>
          <input aria-label="Upload financial statements" {...getInputProps()} />
          <CardContent>
            <Stack alignItems="center" spacing={1} py={3}>
              <Typography variant="inherit" color={isDragActive ? 'primary' : 'textSecondary'}>
                <CloudUploadOutlinedIcon />
              </Typography>
              <Typography variant="body1">Drag and drop or browse</Typography>
              <Typography variant="body2" color="textSecondary">
                Upload as many files as you need
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Up to {MAX_FINANCIAL_DOCUMENTS} PDFs, {MAX_FINANCIAL_DOCUMENT_SIZE_MB} MB each
              </Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>

      {errors.length > 0 && (
        <Box mt={1}>
          {errors.map((error) => (
            <Typography variant="inherit" color="error" component="p" key={error}>
              {error}
            </Typography>
          ))}
        </Box>
      )}

      {files.length > 0 && (
        <Stack spacing={1} mt={2}>
          {files.map((file, index) => (
            <Card variant="outlined" key={`${identity(file)}-${index}`}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="inherit"
                    color="textSecondary"
                    data-private
                    sx={{ mr: 'auto', overflowWrap: 'anywhere' }}
                  >
                    {file.name}
                  </Typography>

                  <Button
                    variant="text"
                    size="large"
                    type="button"
                    color={'danger' as ButtonProps['color']}
                    onClick={removeFile(index)}
                    disabled={submitting}
                    aria-label={`Remove ${file.name}`}
                    sx={{ ml: 2 }}
                  >
                    remove
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {submitting && (
        <Box mt={3}>
          <LinearProgress
            variant={progressComputable ? 'determinate' : 'indeterminate'}
            value={progress}
            aria-label="Upload progress"
          />
          {progressComputable && (
            <Typography variant="caption" color="textSecondary">
              Uploading… {progress}%
            </Typography>
          )}
        </Box>
      )}

      <SubmitWorkflowButton
        progress={submitting}
        disabled={disabled || files.length === 0}
        sx={{ mt: 4 }}
        type="submit"
      >
        Submit
      </SubmitWorkflowButton>
    </form>
  );
};

export { FinancialDocumentsUpload };
